import { useMemo, useState } from "react";
import AdminLayout from "@/layouts/AdminLayout";

import { FiUsers, FiMap, FiHome, FiPieChart, FiAlertCircle, FiCheckSquare, FiEye, FiX, FiTruck } from "react-icons/fi";
import { FaShip } from "react-icons/fa";
import { FaTicket, FaVanShuttle } from "react-icons/fa6";

import DonutChart from "@/components/shared/charts/DonutChart";
import type { Slice } from "@/components/shared/charts/DonutChart";

import KpiCard from "@/components/dashboard/KpiCard";
import FilterRow from "@/components/dashboard/FilterRow";
import { buildYearOptions } from "@/components/dashboard/constants";

import { useAdminDashboardSummary } from "@/api/features/dashboard";
import { useUsers } from "@/api/features/users/queries";
import type { PaginatedUsers, UserResult } from "@/api/features/users/types";
import { getAuth } from "@/api/client";
import { useToursList } from "@/api/features/tours";
import { useSuppliers } from "@/api/features/suppliers/queries";

const PALETTE = [
  "#0ea5e9",
  "#22c55e",
  "#ef4444",
  "#6366f1",
  "#8b5cf6",
  "#f59e0b",
  "#14b8a6",
  "#e11d48",
  "#64748b",
];

function toSlices(byStatus: Record<string, number> | undefined | null): Slice[] {
  const obj = byStatus ?? {};
  return Object.entries(obj)
    .filter(([, value]) => Number(value) > 0)
    .map(([label, value], i) => ({
      label,
      value: Number(value) || 0,
      color: PALETTE[i % PALETTE.length],
    }));
}


function hasNonZeroSlices(data: Slice[] | undefined) {
  return !!data && data.some((s) => Number(s.value) > 0);
}

function sumSlices(data: Slice[] | undefined) {
  if (!data?.length) return 0;
  return data.reduce((acc, s) => acc + (Number(s.value) || 0), 0);
}

/* =========================
 * Sexy UI Bits
 * ========================= */

function IconChip({
  tone = "sky",
  children,
}: {
  tone?: "sky" | "emerald" | "rose" | "violet" | "amber" | "indigo";
  children: React.ReactNode;
}) {
  const toneMap: Record<string, string> = {
    sky: "bg-sky-500/15 text-sky-600 ring-sky-400/25",
    emerald: "bg-emerald-500/15 text-emerald-600 ring-emerald-400/25",
    rose: "bg-rose-500/15 text-rose-600 ring-rose-400/25",
    violet: "bg-violet-500/15 text-violet-600 ring-violet-400/25",
    amber: "bg-amber-500/15 text-amber-600 ring-amber-400/25",
    indigo: "bg-indigo-500/15 text-indigo-600 ring-indigo-400/25",
  };

  return (
    <span
      className={[
        "inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1",
        toneMap[tone],
      ].join(" ")}
    >
      {children}
    </span>
  );
}

/** Page header — title, subtitle, and an optional right-side slot. */
function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 border-b border-zinc-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">{title}</h1>
        {subtitle ? (
          <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>
        ) : null}
      </div>
      {right ? <div className="flex items-center gap-2">{right}</div> : null}
    </div>
  );
}

function ChartCard({
  accent = "sky",
  title,
  subtitle,
  metaLeft,
  metaRight,
  filter,
  children,
}: {
  accent?: "sky" | "emerald" | "rose" | "violet" | "amber" | "indigo";
  title: string;
  subtitle?: string;
  metaLeft?: string;
  metaRight?: React.ReactNode;
  /** Compact controls shown in the header, beside the total. */
  filter?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      {/* One line: the title block gives way (truncating) so the controls and
          total never wrap onto a second row. */}
      <div className="flex items-center justify-between gap-3 border-b border-zinc-100 px-5 py-3.5">
        <div className="flex min-w-0 items-center gap-3">
          <IconChip tone={accent}>
            <FiPieChart className="text-base" />
          </IconChip>

          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-zinc-900">
              {title}
            </div>
            {subtitle ? (
              <div className="mt-0.5 truncate text-xs text-zinc-500">
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2.5">
          {filter}

          {(metaLeft || metaRight != null) ? (
            <div className="border-l border-zinc-100 pl-2.5 text-right">
              {metaLeft ? (
                <div className="text-[10px] uppercase tracking-wider text-zinc-400">
                  {metaLeft}
                </div>
              ) : null}
              {/* `!= null` on purpose: a total of 0 must still render. */}
              {metaRight != null ? (
                <div className="text-lg font-semibold leading-tight tabular-nums text-zinc-900">
                  {metaRight}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="p-5">{children}</div>
    </div>
  );
}

function SexyLoading({  }: { label: string }) {
  return (
    <div className="flex h-44 items-center gap-6">
      <div className="h-32 w-32 shrink-0 animate-pulse rounded-full bg-zinc-100" />
      <div className="flex-1 space-y-2.5">
        <div className="h-3 w-3/4 animate-pulse rounded bg-zinc-100" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-zinc-100" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-zinc-100" />
      </div>
    </div>
  );
}


function SexyEmpty({
  accent = "sky",
  title,
  subtitle,
}: {
  accent?: "sky" | "emerald" | "rose" | "violet" | "amber" | "indigo";
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex h-44 flex-col items-center justify-center text-center">
      <IconChip tone={accent}>
        <FiPieChart className="text-base" />
      </IconChip>
      <div className="mt-3 text-sm font-semibold text-zinc-900">{title}</div>
      <div className="mt-1 text-xs text-zinc-500">{subtitle}</div>
    </div>
  );
}

function SexyError({
  title,
  onRetry,
}: {
  title: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex h-44 flex-col items-center justify-center text-center">
      <span className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-rose-500/15 text-rose-600 ring-1 ring-rose-400/25">
        <FiAlertCircle className="text-lg" />
      </span>
      <div className="text-sm font-semibold text-zinc-900">{title}</div>
      <div className="mt-1 text-xs text-zinc-500">Couldn’t load data. Try again.</div>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-lg bg-zinc-900 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-zinc-800"
      >
        Retry
      </button>
    </div>
  );
}

/** ---------- Page ---------- */
export default function Dashboard() {
  const authUser: any = getAuth()?.user ?? null;
  const roleName = String(authUser?.roleName ?? "").trim();
  const isSupplier = roleName === "Supplier";
  const isAffiliate = roleName === "Affiliate Partner";

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");

  const [leadsMonth, setLeadsMonth] = useState<string>(currentMonth);
  const [leadsYear, setLeadsYear] = useState<number>(currentYear);
  const [leadsEmployeeId, setLeadsEmployeeId] = useState<string>("");

  const [bookMonth, setBookMonth] = useState<string>(currentMonth);
  const [bookYear, setBookYear] = useState<number>(currentYear);

  const { data: usersData } = useUsers(
    { page: 1, limit: 200 },
    { enabled: !isSupplier }
  );

  const employeeOptions = useMemo(
    () =>
      ((usersData as PaginatedUsers | undefined)?.items ?? []).map((u: UserResult) => {
        const fullName = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
        return {
          value: String(u.id),
          label: fullName || u.email || u.username || `User #${u.id}`,
        };
      }),
    [usersData]
  );

  const leadsQ = useAdminDashboardSummary(
    {
      month: Number(leadsMonth),
      year: leadsYear,
      employeeId: leadsEmployeeId ? Number(leadsEmployeeId) : undefined,
    },
    { enabled: !isSupplier }
  );
  const bookingsQ = useAdminDashboardSummary(
    { month: Number(bookMonth), year: bookYear },
    { enabled: !isSupplier }
  );

  // Suppliers count (the dashboard summary endpoint doesn't include it, so we
  // read the total off a minimal suppliers list query).
  const suppliersQ = useSuppliers({ page: 1, limit: 1 });
  const suppliersCount = suppliersQ.data?.total ?? 0;

  const cards =
    leadsQ.data?.cards ??
    bookingsQ.data?.cards ?? {
      customers: 0,
      tours: 0,
      hotels: 0,
      cruise: 0,
      attractions: 0,
      transfer: 0,
    };

  const leadsData: Slice[] = useMemo(
    () => toSlices(leadsQ.data?.leads?.byStatus),
    [leadsQ.data?.leads?.byStatus]
  );

  const bookingsData: Slice[] = useMemo(
    () => toSlices(bookingsQ.data?.bookings?.byStatus),
    [bookingsQ.data?.bookings?.byStatus]
  );

  const leadsTotal = leadsQ.data?.leads?.total ?? sumSlices(leadsData);
  const bookingsTotal = bookingsQ.data?.bookings?.total ?? sumSlices(bookingsData);

  const yearOptions = useMemo(() => buildYearOptions(6), []);

  if (isSupplier) {
    return <SupplierDashboard />;
  }

  if (isAffiliate) {
    return <AffiliateDashboard />;
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of customers, suppliers, inventory and activity."
      />

      {/* KPI Cards */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
        <KpiCard title="Customers" value={cards.customers} icon={<FiUsers />} color="bg-indigo-500" />
        <KpiCard title="Suppliers" value={suppliersCount} icon={<FiTruck />} color="bg-sky-500" />
        <KpiCard title="Tours" value={cards.tours} icon={<FiMap />} color="bg-emerald-500" />
        <KpiCard title="Hotels" value={cards.hotels} icon={<FiHome />} color="bg-rose-500" />
        <KpiCard title="Cruise" value={cards.cruise} icon={<FaShip />} color="bg-amber-500" />
        <KpiCard title="Attractions / Activities" value={cards.attractions} icon={<FaTicket />} color="bg-violet-500" />
        <KpiCard title="Transfer" value={cards.transfer} icon={<FaVanShuttle />} color="bg-sky-500" />
      </section>

      {/* Charts */}
      <section className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
        {/* Leads */}
        <ChartCard
          accent="violet"
          title="Leads Status"
          subtitle="Distribution by status"
          metaLeft="Total"
          metaRight={leadsTotal}
          filter={
            <FilterRow
              title="Leads Status"
              month={leadsMonth}
              year={leadsYear}
              onMonthChange={setLeadsMonth}
              onYearChange={setLeadsYear}
              yearOptions={yearOptions}
              employee={{
                value: leadsEmployeeId,
                onChange: setLeadsEmployeeId,
                options: employeeOptions,
              }}
            />
          }
        >
          <div>
            {leadsQ.isLoading ? (
              <SexyLoading label="Leads Status" />
            ) : leadsQ.isError ? (
              <SexyError title="Leads Status" onRetry={() => leadsQ.refetch()} />
            ) : hasNonZeroSlices(leadsData) ? (
              <DonutChart title="Leads Status" subtitle="By status" data={leadsData} embedded />
            ) : (
              <SexyEmpty accent="violet" title="No leads" subtitle="No records for selected period" />
            )}
          </div>
        </ChartCard>

        {/* Bookings */}
        <ChartCard
          accent="sky"
          title="Bookings Summary"
          subtitle="Distribution by status"
          metaLeft="Total"
          metaRight={bookingsTotal}
          filter={
            <FilterRow
              title="Bookings Summary"
              month={bookMonth}
              year={bookYear}
              onMonthChange={setBookMonth}
              onYearChange={setBookYear}
              yearOptions={yearOptions}
            />
          }
        >
          <div>
            {bookingsQ.isLoading ? (
              <SexyLoading label="Bookings Summary" />
            ) : bookingsQ.isError ? (
              <SexyError title="Bookings Summary" onRetry={() => bookingsQ.refetch()} />
            ) : hasNonZeroSlices(bookingsData) ? (
              <DonutChart title="Bookings Summary" subtitle="By status" data={bookingsData} embedded />
            ) : (
              <SexyEmpty accent="sky" title="No bookings" subtitle="No records for selected period" />
            )}
          </div>
        </ChartCard>
      </section>
    </AdminLayout>
  );
}

function AffiliateDashboard() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = String(now.getMonth() + 1).padStart(2, "0");

  const [leadsMonth, setLeadsMonth] = useState<string>(currentMonth);
  const [leadsYear, setLeadsYear] = useState<number>(currentYear);
  const [bookMonth, setBookMonth] = useState<string>(currentMonth);
  const [bookYear, setBookYear] = useState<number>(currentYear);

  const leadsQ = useAdminDashboardSummary({
    month: Number(leadsMonth),
    year: leadsYear,
  });
  const bookingsQ = useAdminDashboardSummary({
    month: Number(bookMonth),
    year: bookYear,
  });

  const customerCount = leadsQ.data?.cards?.customers ?? bookingsQ.data?.cards?.customers ?? 0;
  const leadCount = leadsQ.data?.leads?.total ?? 0;
  const bookingCount = bookingsQ.data?.bookings?.total ?? 0;

  const leadsData: Slice[] = useMemo(
    () => toSlices(leadsQ.data?.leads?.byStatus),
    [leadsQ.data?.leads?.byStatus]
  );
  const bookingsData: Slice[] = useMemo(
    () => toSlices(bookingsQ.data?.bookings?.byStatus),
    [bookingsQ.data?.bookings?.byStatus]
  );

  const yearOptions = useMemo(() => buildYearOptions(6), []);

  return (
    <AdminLayout>
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Affiliate Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Your own leads, customers, and bookings summary.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <KpiCard
          title="My Leads"
          value={leadCount}
          icon={<FiCheckSquare />}
          color="bg-violet-500"
        />
        <KpiCard
          title="My Customers"
          value={customerCount}
          icon={<FiUsers />}
          color="bg-sky-500"
        />
        <KpiCard
          title="My Bookings"
          value={bookingCount}
          icon={<FiMap />}
          color="bg-emerald-500"
        />
      </section>

      <section className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <ChartCard
          accent="violet"
          title="My Leads"
          subtitle="Distribution by status"
          metaLeft="Total"
          metaRight={leadCount}
        >
          <FilterRow
            title="My Leads"
            month={leadsMonth}
            year={leadsYear}
            onMonthChange={setLeadsMonth}
            onYearChange={setLeadsYear}
            yearOptions={yearOptions}
          />

          <div className="mt-3">
            {leadsQ.isLoading ? (
              <SexyLoading label="My Leads" />
            ) : leadsQ.isError ? (
              <SexyError title="My Leads" onRetry={() => leadsQ.refetch()} />
            ) : hasNonZeroSlices(leadsData) ? (
              <DonutChart title="My Leads" subtitle="By status" data={leadsData} embedded />
            ) : (
              <SexyEmpty accent="violet" title="No leads" subtitle="No leads for selected period" />
            )}
          </div>
        </ChartCard>

        <ChartCard
          accent="sky"
          title="My Bookings"
          subtitle="Distribution by status"
          metaLeft="Total"
          metaRight={bookingCount}
        >
          <FilterRow
            title="My Bookings"
            month={bookMonth}
            year={bookYear}
            onMonthChange={setBookMonth}
            onYearChange={setBookYear}
            yearOptions={yearOptions}
          />

          <div className="mt-3">
            {bookingsQ.isLoading ? (
              <SexyLoading label="My Bookings" />
            ) : bookingsQ.isError ? (
              <SexyError title="My Bookings" onRetry={() => bookingsQ.refetch()} />
            ) : hasNonZeroSlices(bookingsData) ? (
              <DonutChart title="My Bookings" subtitle="By status" data={bookingsData} embedded />
            ) : (
              <SexyEmpty accent="sky" title="No bookings" subtitle="No bookings for selected period" />
            )}
          </div>
        </ChartCard>
      </section>
    </AdminLayout>
  );
}

function SupplierDashboard() {
  const allToursQ = useToursList({ page: 1, pageSize: 1 });
  const activeToursQ = useToursList({ page: 1, pageSize: 1, status: "ACTIVE" });
  const draftToursQ = useToursList({ page: 1, pageSize: 1, status: "DRAFT" });
  const inactiveToursQ = useToursList({ page: 1, pageSize: 1, status: "INACTIVE" });
  const latestToursQ = useToursList({ page: 1, pageSize: 5 });

  const latest = latestToursQ.data?.items ?? [];

  return (
    <AdminLayout>
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Supplier Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Your upload and listing summary.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Tours Uploaded"
          value={allToursQ.data?.total ?? 0}
          icon={<FiMap />}
          color="bg-sky-500"
        />
        <KpiCard
          title="Active Tours"
          value={activeToursQ.data?.total ?? 0}
          icon={<FiCheckSquare />}
          color="bg-emerald-500"
        />
        <KpiCard
          title="Draft Tours"
          value={draftToursQ.data?.total ?? 0}
          icon={<FiEye />}
          color="bg-amber-500"
        />
        <KpiCard
          title="Inactive Tours"
          value={inactiveToursQ.data?.total ?? 0}
          icon={<FiX />}
          color="bg-rose-500"
        />
      </section>

      <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <h2 className="text-base font-semibold text-gray-900">Recent Tours</h2>
        <p className="mt-1 text-xs text-gray-500">Your latest uploaded listings.</p>

        {latestToursQ.isLoading && (
          <div className="mt-4 text-sm text-gray-600">Loading your tours…</div>
        )}

        {latestToursQ.error && (
          <div className="mt-4 rounded-md border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            Failed to load your tours.
          </div>
        )}

        {!latestToursQ.isLoading && !latestToursQ.error && latest.length === 0 && (
          <div className="mt-4 text-sm text-gray-600">No tours uploaded yet.</div>
        )}

        {!latestToursQ.isLoading && !latestToursQ.error && latest.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-2 py-2">Tour Name</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Nights/Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {latest.map((t: any) => (
                  <tr key={t.id}>
                    <td className="px-2 py-2 text-gray-800">{t.name}</td>
                    <td className="px-2 py-2 text-gray-700">{t.status}</td>
                    <td className="px-2 py-2 text-gray-700">
                      {t.nights}N / {t.days}D
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}
